import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ThemeConfig } from 'src/app/shared/models/theme-config';
import { ConstantUtil } from 'src/app/util/constant.util';

// 使用共享的 ThemeConfig（canonical schema）

@Component({
  selector: 'app-theme-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzCardModule,
    NzSpinModule,
    NzSelectModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './theme-item.component.html',
  styleUrls: ['./theme-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeItemComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  loading = false;
  saving = false;
  toggling = false;
  themeId: string | null = null;

  name = '';
  description = '';
  // 主题状态显示与控制
  status: 'Disable' | 'Enable' | 'New' = 'New';
  // 是否为默认主题
  isDefault: boolean = false;

  // 阴影预设（供界面选择）：none/light/standard/deep/custom
  shadowPreset: 'none' | 'light' | 'standard' | 'deep' | 'custom' = 'custom';
  // 自定义阴影值（当选择 custom 时可编辑）
  shadowCustomValue: string = '';
  // 预设映射：在模板中也会用到，因此保持为公开属性
  shadowPresetValues: Record<'none' | 'light' | 'standard' | 'deep', string> = {
    none: 'none',
    light: '0 2px 8px rgba(0,0,0,0.08)',
    standard: '0 4px 12px rgba(0,0,0,0.12)',
    deep: '0 8px 24px rgba(0,0,0,0.16)'
  };

  themeConfig: ThemeConfig = this.getDefaultThemeConfig();

  private getDefaultThemeConfig(): ThemeConfig {
    return {
      primaryColor: '#1890FF',
      backgroundColor: '#F5F7FA',
      headerTextColor: '#FFFFFF',
      customerBubbleBg: '#E6F7FF',
      customerBubbleText: '#1F2937',
      agentBubbleBg: '#FAFAFA',
      agentBubbleText: '#1F2937',
      headerBg: '#1890FF',
      footerBg: '#FFFFFF',
      bubbleRadius: '8px',
      windowRadius: '12px',
      boxShadow: { medium: '0 4px 12px rgba(0,0,0,0.15)' },
      fontSize: { base: '16px' }
    };
  }

  private mergeThemeConfig(partial: any): ThemeConfig {
    const def = this.getDefaultThemeConfig();
    return {
      ...def,
      ...(partial || {}),
      boxShadow: { ...def.boxShadow, ...(partial?.boxShadow || {}) },
      fontSize: { ...def.fontSize, ...(partial?.fontSize || {}) }
    } as ThemeConfig;
  }

  ngOnInit(): void {
    // 支持 /themes/item 与 /themes/item/:id 两种路由
    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      this.themeId = id;
      if (this.themeId) {
        this.loadDetail(this.themeId);
      } else {
        // 无ID时按默认配置计算预设
        this.updateShadowPresetFromConfig();
      }
    });
  }

  loadDetail(id: string): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_ITEM}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          const data = res?.data;
          if (data) {
            this.name = data.name || '';
            this.description = data.description || '';
            this.status = (data.status as any) || 'New';
            this.isDefault = !!(data.isDefault ?? data.default);
            try {
              const cfgStr = data.configJson || data.config || data.themeJson;
              if (cfgStr) {
                const parsed = typeof cfgStr === 'string' ? JSON.parse(cfgStr) : cfgStr;
                this.themeConfig = this.mergeThemeConfig(parsed);
                this.updateShadowPresetFromConfig();
              }
            } catch {
              // ignore parse errors and keep defaults
              this.themeConfig = this.getDefaultThemeConfig();
              this.updateShadowPresetFromConfig();
            }
          } else {
            this.message.error(this.i18n.fanyi('theme.item.load-failed'));
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('theme.item.load-failed'));
        }
      });
  }

  private updateShadowPresetFromConfig(): void {
    const m = (this.themeConfig.boxShadow.medium || '').trim();
    const matchKey = (Object.keys(this.shadowPresetValues) as Array<'none' | 'light' | 'standard' | 'deep'>).find(
      k => this.shadowPresetValues[k] === m
    );
    if (matchKey) {
      this.shadowPreset = matchKey;
      this.shadowCustomValue = '';
    } else if (m === 'none') {
      this.shadowPreset = 'none';
      this.shadowCustomValue = '';
    } else {
      this.shadowPreset = 'custom';
      this.shadowCustomValue = m;
    }
  }

  onShadowPresetChange(val: 'none' | 'light' | 'standard' | 'deep' | 'custom'): void {
    this.shadowPreset = val;
    if (val === 'custom') {
      // 保持当前自定义值（若为空，则使用标准阴影作为初始值）
      if (!this.shadowCustomValue?.trim()) {
        this.shadowCustomValue = this.shadowPresetValues.standard;
      }
      this.themeConfig.boxShadow.medium = this.shadowCustomValue;
    } else {
      const value = this.shadowPresetValues[val];
      this.themeConfig.boxShadow.medium = value;
    }
  }

  onShadowCustomValueChange(val: string): void {
    this.shadowCustomValue = val;
    if (this.shadowPreset === 'custom') {
      this.themeConfig.boxShadow.medium = val;
    }
  }

  save(): void {
    if (!this.name?.trim()) {
      this.message.warning(this.i18n.fanyi('theme.list.message.name-required'));
      return;
    }
    this.saving = true;
    const body: any = { name: this.name?.trim(), description: this.description?.trim() };
    let url = ConstantUtil.BASE_THEME_V1_CREATE;
    if (this.themeId) {
      body.id = this.themeId;
      url = ConstantUtil.BASE_THEME_V1_UPDATE;
    }
    // attach theme config JSON
    try {
      body.configJson = JSON.stringify(this.themeConfig);
    } catch {
      body.configJson = '';
    }
    this.http
      .post(url, body)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (res?.success) {
            this.message.success(this.i18n.fanyi(this.themeId ? 'theme.item.save-success-edit' : 'theme.item.save-success-create'));
            this.router.navigateByUrl('/themes');
          } else {
            this.message.error(res?.msg ?? this.i18n.fanyi('theme.item.save-failed'));
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('theme.item.save-failed'));
        }
      });
  }

  back(): void {
    this.router.navigateByUrl('/themes');
  }

  // 状态颜色映射（与列表页一致）
  getStatusColor(status?: 'Disable' | 'Enable' | 'New'): string {
    switch (status) {
      case 'Enable':
        return 'green';
      case 'Disable':
        return 'red';
      case 'New':
        return 'blue';
      default:
        return 'default';
    }
  }

  getStatusText(status?: 'Disable' | 'Enable' | 'New'): string {
    const map: Record<string, { key: string; fallback: string }> = {
      Enable: { key: 'theme.status.enable', fallback: '启用' },
      Disable: { key: 'theme.status.disable', fallback: '禁用' },
      New: { key: 'theme.status.new', fallback: '新建' }
    };
    const s = status || 'New';
    const conf = map[s] || map['New'];
    const label = this.i18n.fanyi(conf.key);
    return label === conf.key ? conf.fallback : label;
  }

  shouldShowEnableButton(): boolean {
    return !!this.themeId && this.status !== 'Enable';
  }

  shouldShowDisableButton(): boolean {
    return !!this.themeId && this.status === 'Enable';
  }

  // 是否显示设为默认按钮
  shouldShowSetDefaultButton(): boolean {
    return !!this.themeId && !this.isDefault;
  }

  enable(): void {
    if (!this.themeId) return;
    this.toggling = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_ENABLE}/${this.themeId}`, {})
      .pipe(
        finalize(() => {
          this.toggling = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.status = 'Enable';
          this.message.success(this.i18n.fanyi('theme.list.message.enable-success'));
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }

  // 设为默认主题
  setAsDefault(): void {
    if (!this.themeId) return;
    this.toggling = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_DEFAULT_SET}/${this.themeId}`, {})
      .pipe(
        finalize(() => {
          this.toggling = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.isDefault = true;
          this.message.success(this.i18n.fanyi('theme.item.set-default-success'));
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }

  disable(): void {
    if (!this.themeId) return;
    this.toggling = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_DISABLE}/${this.themeId}`, {})
      .pipe(
        finalize(() => {
          this.toggling = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.status = 'Disable';
          this.message.success(this.i18n.fanyi('theme.list.message.disable-success'));
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }
}
